import { pluralize } from './pluralize';
import { pluralizeWord } from './pluralizeWord';

export type DurationUnit = 'day' | 'hour' | 'minute';

export interface Duration {
  quantity: number;
  unit: DurationUnit;
}

type Options = {
  format?: 'short' | 'long'; // Short: 24h, Long: 24 hours
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * This will round a value if it is less than 0.0001 or greater than 0.9999.
 * This can be useful when performing operations on numbers that result in
 * very large or very small remainders, when we would except an integer.
 */
const clamp = (value: number) => (value % 1 < 0.0001 || value % 1 > 0.9999 ? Math.round(value) : value);
const clampTo3FractionDigits = (ms: number) => clamp(Number(ms.toFixed(3)));

/**
 * Canonical ms → Duration converter for rollouts (progressive, guarded, release builder/viewer).
 * Picks the largest unit that divides evenly so user-entered durations round-trip through
 * persistence without changing unit (e.g. `4 hour` → 14_400_000 ms → `4 hour`, not `240 minute`).
 * Falls back to rounded minutes for values that don't divide evenly into hours.
 *
 * For human-readable formatting use {@link getDisplayDurationFromMs} instead — it picks the
 * largest unit ≤ the value (allowing fractional output) which is appropriate for display but
 * breaks editor round-trips.
 */
export function getDurationFromMs(ms: number): Duration {
  if (Number.isFinite(ms) && ms !== 0 && ms % DAY_MS === 0) {
    return { quantity: ms / DAY_MS, unit: 'day' };
  }
  if (Number.isFinite(ms) && ms !== 0 && ms % HOUR_MS === 0) {
    return { quantity: ms / HOUR_MS, unit: 'hour' };
  }
  if (!Number.isFinite(ms) || ms <= 0) {
    return { quantity: 0, unit: 'minute' };
  }
  return { quantity: Math.round(ms / MINUTE_MS), unit: 'minute' };
}

/**
 * Inverse of {@link getDurationFromMs}. Treats NaN/Infinity quantities as 0.
 */
export function getMsFromDuration(duration: Duration) {
  if (Number.isNaN(duration.quantity) || !Number.isFinite(duration.quantity)) {
    return 0;
  }

  switch (duration.unit) {
    case 'minute':
      return Math.round(duration.quantity * MINUTE_MS);
    case 'hour':
      return Math.round(duration.quantity * HOUR_MS);
    case 'day':
      return Math.round(duration.quantity * DAY_MS);
    default:
      break;
  }

  // oxlint-disable-next-line no-console
  console.error(`ERROR: getMsFromDuration cannot convert duration ${JSON.stringify(duration)}`);

  return 0;
}

export function getDisplayDurationFromMs(ms: number, { format = 'long' }: Options = {}): string {
  if (ms < MINUTE_MS) {
    return format === 'long' ? 'less than one minute' : '<1m';
  }

  const parsedDuration = pickDisplayDuration(ms);

  return getDurationDisplayValue(parsedDuration, { format });
}

/**
 * TODO: Remove once we've deleted all the code that uses this Duration type
 */
export function getDisplayDurationFromParsedDurations(parsedDurations: Duration[], { format = 'long' }: Options = {}) {
  const stageDurationsMs = parsedDurations.map((d) => getMsFromDuration(d));
  const totalDurationMs = stageDurationsMs.reduce((acc, cur) => cur + acc, 0);

  return getDisplayDurationFromMs(totalDurationMs, { format });
}

/**
 * Picks the largest unit that is ≤ the value. Allows fractional output (clamped to 3 decimals)
 * for non-evenly-dividing values — `getDurationDisplayValue` then breaks the fractional part
 * down into the next-smaller unit ("2 days 12 hours"). Internal to display formatting.
 */
function pickDisplayDuration(ms: number): Duration {
  const minutes = ms / MINUTE_MS;
  if (minutes < 60) {
    return {
      quantity: Math.round(minutes), // fractional minutes are not allowed
      unit: 'minute',
    };
  }

  const hours = minutes / 60;
  if (hours < 24) {
    return {
      quantity: clampTo3FractionDigits(hours),
      unit: 'hour',
    };
  }

  return {
    quantity: clampTo3FractionDigits(hours / 24),
    unit: 'day',
  };
}

function getDurationDisplayValue(duration: Duration, { format = 'long' }: Options) {
  const rawQuantity = clampTo3FractionDigits(duration.quantity);

  if (duration.unit === 'day') {
    const days = Math.floor(rawQuantity);
    const remainingHours = (rawQuantity - days) * 24;
    const hours = Math.round(remainingHours);

    // If hours round up to 24, convert to an additional day
    if (hours === 24) {
      return format === 'long' ? `${days + 1} ${pluralize(days + 1, 'day', 'days')}` : `${days + 1}d`;
    }

    const parts: string[] = [];
    parts.push(format === 'long' ? `${days} ${pluralize(days, 'day', 'days')}` : `${days}d`);

    if (hours > 0) {
      parts.push(format === 'long' ? `${hours} ${pluralize(hours, 'hour', 'hours')}` : `${hours}h`);
    }

    return parts.join(' ');
  }

  if (duration.unit === 'hour') {
    const hours = Math.floor(rawQuantity);
    const minutes = Math.round((rawQuantity - hours) * 60);

    // If minutes round up to 60, convert to an additional hour
    if (minutes === 60) {
      return format === 'long' ? `${hours + 1} ${pluralize(hours + 1, 'hour', 'hours')}` : `${hours + 1}h`;
    }

    const parts: string[] = [];
    parts.push(format === 'long' ? `${hours} ${pluralize(hours, 'hour', 'hours')}` : `${hours}h`);

    if (minutes > 0) {
      parts.push(format === 'long' ? `${minutes} ${pluralize(minutes, 'minute', 'minutes')}` : `${minutes}m`);
    }

    return parts.join(' ');
  }

  const quantity = Math.round(rawQuantity); // round to nearest integer for minutes
  return format === 'long'
    ? `${quantity} ${pluralize(quantity, duration.unit, pluralizeWord(duration.unit))}`
    : `${quantity}${duration.unit[0]}`;
}
